import { useTranslation } from 'react-i18next';
import { useSettings } from '@/hooks/useProducts';
import { AlertTriangle } from 'lucide-react';

interface LowStockIndicatorProps {
  availableQuantity: number;
  stockStatus?: string;
}

export default function LowStockIndicator({ availableQuantity, stockStatus }: LowStockIndicatorProps) {
  const { t } = useTranslation('upsell');
  const { data } = useSettings();
  const config = data?.settings?.upsell_config?.low_stock_indicator;

  if (!config?.enabled) {
    if (stockStatus === 'out_of_stock' || availableQuantity <= 0) {
      return <p className="text-sm font-medium text-red-600">{t('lowStock.outOfStock')}</p>;
    }
    if (availableQuantity <= 10 && availableQuantity > 0) {
      return (
        <p className="text-sm text-amber-700">
          {t('lowStock.onlyXLeft', { count: availableQuantity })}
        </p>
      );
    }
    return null;
  }

  if (availableQuantity <= 0) {
    return <p className="text-sm font-medium text-red-600">{t('lowStock.outOfStock')}</p>;
  }

  const threshold = config.threshold || 10;
  if (availableQuantity > threshold) return null;

  let message: string;
  if (config.show_exact_count) {
    message = config.message_template
      ? (config.message_template as string).replace('{count}', String(availableQuantity))
      : t('lowStock.onlyXLeftUrgent', { count: availableQuantity });
  } else {
    message = t('lowStock.defaultLowStock');
  }

  return (
    <div className="flex items-center gap-1.5 text-sm text-amber-700 animate-pulse">
      <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="font-medium">{message}</span>
    </div>
  );
}
